import { Base } from "../../framework/base";
import { IGetTitle } from "../common/idTitle";
import { TaskDataRowDataType } from "../enums";
import { ITaskDataRow } from "../task/taskDataRow";
import { ITaskDataRowCol, TaskDataRowCol } from "../task/taskDataRowCol";
import { ITaskTypeCol, TaskTypeCol } from "../task/taskTypeCol";

export interface IBaseSchemaParseResult {
    formDataRow: ITaskDataRow;
    error: string;
}

export interface IBaseFormModel extends IGetTitle {
    id: string;
    code: string;
    formCols: ITaskTypeCol[];

    getHash(): string;
    hasFilterColumnsBase(cols: ITaskTypeCol[]): boolean;
}

export abstract class BaseFormModel implements IBaseFormModel {
    id: string;
    code: string;
    formCols: TaskTypeCol[];

    constructor();
    constructor(obj: IBaseFormModel);
    constructor(obj?: any) {
        this.id = obj && obj.id || "";
        this.code = obj && obj.code || "";
        this.formCols = Base.getTypedArray<TaskTypeCol>(obj ? obj.formCols : null, TaskTypeCol);
    }

    getTitle(): string {
        return this.code;
    }

    getHash(): string {
        return this.id + "#" + this.code;
    }

    hasFilterColumnsBase(cols: ITaskTypeCol[]): boolean {
        for (let i = 0; i < cols.length; i++) {
            if (cols[i].useInFilter) {
                return true;
            }
        }
        return false;
    }

    //#region Expression Evaluation
    static evaluateColsExpression = (expression: string, typeCols: ITaskTypeCol[], taskDataRowCols: ITaskDataRowCol[], errorCallback: (error) => void): any => {
        if (!expression) return null;
        for (let i = 0; i < taskDataRowCols.length; i++) {
            const dataCol = taskDataRowCols[i];
            const index = expression.indexOf(dataCol.field);
            if (index < 0) continue;
            const typeCol = typeCols.find(j => j.field === dataCol.field);
            if (Base.isNullOrUndefined(typeCol)) continue;
            let valueStr: string = "";
            if (typeCol.dataType === TaskDataRowDataType.String) {
                valueStr = !Base.isNullOrEmpty(dataCol?.valueStr) ? dataCol?.valueStr : "";
            } else if (typeCol.dataType === TaskDataRowDataType.Integer || typeCol.dataType === TaskDataRowDataType.Number || typeCol.dataType === TaskDataRowDataType.Boolean || typeCol.dataType === TaskDataRowDataType.TimeSpan) {
                valueStr = !Base.isNullOrUndefined(dataCol.valueNbr) ? dataCol.valueNbr.toString(10) : "0";
            } else if (typeCol.dataType === TaskDataRowDataType.Time) {
                valueStr = TaskDataRowCol.getTimeEvalValue(dataCol.valueNbr);
            } else if (typeCol.dataType === TaskDataRowDataType.Date) {
                valueStr = TaskDataRowCol.getDateEvalValue(dataCol.valueNbr);
            }
            // console.log("evaluateColsExpression 1", expression, dataCol.field, valueStr);
            expression = expression.replace(new RegExp(dataCol.field, "g"), valueStr);
            // console.log("evaluateColsExpression 2", expression);
        } try {
            /* eslint-disable no-eval */
            return eval(expression);
            /* eslint-enable no-eval */
        } catch (error) {
            errorCallback(error);
            return null;
        }
    };

    static evaluateColsExpressions = (taskDataRowCols: ITaskDataRowCol[], typeCols: ITaskTypeCol[], errorCallback: (error) => void = null) => {
        const callback = errorCallback || ((error) => {
            console.log("evaluateColsExpressions", error);
        });
        if (taskDataRowCols.length < 1) return;
        const relevantTypeCols: ITaskTypeCol[] = typeCols.filter(i => !Base.isNullOrEmpty(i.expression));
        if (relevantTypeCols.length < 1) return;
        TaskTypeCol.sortExpressionTaskTypeCols(relevantTypeCols);
        const expressionTaskDataRowCols = TaskDataRowCol.getExpressionFieldTaskDataRowCols(taskDataRowCols);
        for (let i = 0; i < relevantTypeCols.length; i++) {
            const typeCol = relevantTypeCols[i];
            const dataCol = expressionTaskDataRowCols.find(j => j.field === typeCol.field);
            if (Base.isNullOrUndefined(dataCol)) continue;
            const value = BaseFormModel.evaluateColsExpression(typeCol.expression, typeCols, expressionTaskDataRowCols, callback);
            if (Base.isNullOrUndefined(value)) continue;
            if (typeCol.dataType === TaskDataRowDataType.String) {
                dataCol.valueStr = value;
            } else {
                dataCol.valueNbr = value;
                dataCol.valueStr = TaskDataRowCol.getTaskDataRowColUiValue(typeCols.find(j => j.field === dataCol.field), dataCol?.valueStr, dataCol.valueNbr);
            }
        }
    };
    //#endregion Expression Evaluation

    //#region ReadOnly Expression Evaluation
    static evaluateColsReadOnlyExpressions = (taskDataRowCols: ITaskDataRowCol[], typeCols: ITaskTypeCol[], errorCallback: (error) => void = null): string[] => {
        const callback = errorCallback || ((error) => {
            console.log("evaluateColsReadOnlyExpressions", error);
        });
        const result: string[] = [];
        if (!taskDataRowCols || !typeCols || taskDataRowCols.length < 1 || typeCols.length < 1) return result;
        const relevantTypeCols: ITaskTypeCol[] = typeCols.filter(i => !Base.isNullOrEmpty(i.readOnlyExpression));
        if (relevantTypeCols.length < 1) return result;
        TaskTypeCol.sortExpressionTaskTypeCols(relevantTypeCols);
        const expressionTaskDataRowCols = TaskDataRowCol.getExpressionFieldTaskDataRowCols(taskDataRowCols);
        for (let i = 0; i < relevantTypeCols.length; i++) {
            const typeCol = relevantTypeCols[i];
            const dataCol = expressionTaskDataRowCols.find(j => j.field === typeCol.field);
            if (!dataCol) continue;
            const value = BaseFormModel.evaluateColsExpression(typeCol.readOnlyExpression, typeCols, expressionTaskDataRowCols, callback);
            if (!value) continue;
            result.push(typeCol.field);
        }
        result.sort();
        return result;
    };
    //#endregion ReadOnly Expression Evaluation

    //#region Clear Expression Evaluation
    static evaluateColsClearExpressions = (taskDataRowCols: ITaskDataRowCol[], typeCols: ITaskTypeCol[], errorCallback: (error) => void = null): string[] => {
        const callback = errorCallback || ((error) => {
            console.log("evaluateColsClearExpressions", error);
        });
        const result: string[] = [];
        if (!taskDataRowCols || !typeCols || taskDataRowCols.length < 1 || typeCols.length < 1) return result;
        const relevantTypeCols: ITaskTypeCol[] = typeCols.filter(i => !Base.isNullOrEmpty(i.clearExpression));
        if (relevantTypeCols.length < 1) return result;
        TaskTypeCol.sortExpressionTaskTypeCols(relevantTypeCols);
        const expressionTaskDataRowCols = TaskDataRowCol.getExpressionFieldTaskDataRowCols(taskDataRowCols);
        for (let i = 0; i < relevantTypeCols.length; i++) {
            const typeCol = relevantTypeCols[i];
            const dataCol = expressionTaskDataRowCols.find(j => j.field === typeCol.field);
            if (!dataCol) continue;
            const value = BaseFormModel.evaluateColsExpression(typeCol.clearExpression, typeCols, expressionTaskDataRowCols, callback);
            if (!value) continue;
            result.push(typeCol.field);
        }
        result.sort();
        return result;
    };
    //#endregion Clear Expression Evaluation
}